import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import {
  ShieldCheck,
  Cookie,
  Target,
  Lock,
  Ban,
  Hand,
  Power,
  Mail,
} from "lucide-react";

const LAST_UPDATED = "4 de junho de 2026";
const CONTACT_EMAIL = "suporte@lucralink.com.br";

/**
 * Política de Privacidade pública (sem login).
 * Servida em https://lucralink.com.br/privacidade.
 * URL de referência para a listagem da extensão na Chrome Web Store.
 */
export function PrivacidadePage(): JSX.Element {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* ---- Header ---- */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-bold tracking-tight text-lg text-foreground">
            Lucra<span className="text-primary">Link</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Voltar ao início
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-16 space-y-12">
          <PolicyIntro />

          {/* ---- 1. Dados coletados ---- */}
          <Section
            icon={Cookie}
            title="1. Quais dados a extensão coleta"
          >
            <p>
              A coleta acontece apenas quando você aciona explicitamente a captura de
              sessão (botão <strong className="text-foreground">&ldquo;Conectar sessão Mercado
              Livre&rdquo;</strong> no popup da extensão), com o painel de afiliados do
              Mercado Livre aberto e logado na sua conta. Nesse momento, a extensão coleta:
            </p>
            <ul className="space-y-2.5 pl-1">
              <Item>
                <strong className="text-foreground">Cookies de sessão do Mercado Livre</strong>
                {" "}(incluindo cookies <code className="text-xs">httpOnly</code> de
                autenticação): necessários para identificar a sua conta de afiliado junto ao
                Mercado Livre e gerar links em seu nome.
              </Item>
              <Item>
                <strong className="text-foreground">Token CSRF</strong> da página do painel de
                afiliados: exigido pelo Mercado Livre para que as requisições de geração de
                link sejam aceitas como legítimas.
              </Item>
              <Item>
                <strong className="text-foreground">Contexto de produto</strong> (por exemplo,
                URL e identificadores da página de produto que você está visualizando) e o seu
                identificador de afiliado (tag), usados para montar o link correto.
              </Item>
            </ul>
            <p>
              A captura de <strong className="text-foreground">produto</strong> (envio de um
              link de produto para conversão) <strong className="text-foreground">não coleta
              cookies nem segredos de sessão</strong>. Somente a captura de sessão coleta os
              cookies de autenticação, e apenas mediante a sua ação explícita.
            </p>
          </Section>

          {/* ---- 2. Finalidade ---- */}
          <Section icon={Target} title="2. Para que esses dados são usados">
            <p>
              Os dados coletados têm uma única finalidade:{" "}
              <strong className="text-foreground">
                gerar os links de afiliado da sua própria conta
              </strong>{" "}
              de forma automática a partir de links de produto. A sessão capturada permite que
              a plataforma LucraLink chame as APIs do Mercado Livre em seu nome, como se você
              mesmo estivesse gerando o link no painel de afiliados.
            </p>
            <p>
              A extensão não usa esses dados para perfilamento, publicidade, venda de dados ou
              qualquer finalidade não relacionada à geração dos seus links de afiliado.
            </p>
          </Section>

          {/* ---- 3. Destino e armazenamento ---- */}
          <Section icon={Lock} title="3. Para onde os dados vão e como são guardados">
            <p>
              Os dados de sessão são enviados diretamente da extensão para o backend do
              LucraLink, hospedado no{" "}
              <strong className="text-foreground">Supabase</strong>, por meio de uma chamada
              autenticada (RPC). A sessão é guardada de forma{" "}
              <strong className="text-foreground">criptografada no Vault do Supabase</strong>,
              um cofre de segredos com acesso restrito. Os cookies e o token CSRF nunca ficam
              expostos no painel nem em logs.
            </p>
            <p>
              A transmissão ocorre exclusivamente por conexões seguras (HTTPS). O acesso à
              sessão é limitado aos serviços do LucraLink estritamente necessários para gerar
              os seus links.
            </p>
          </Section>

          {/* ---- 4. Compartilhamento ---- */}
          <Section icon={Ban} title="4. Compartilhamento com terceiros">
            <p>
              O LucraLink{" "}
              <strong className="text-foreground">não compartilha, vende nem aluga</strong> os
              seus cookies de sessão, o token CSRF ou qualquer dado coletado pela extensão a
              terceiros. Os dados são usados somente entre a extensão e a infraestrutura do
              próprio LucraLink, com a finalidade descrita acima.
            </p>
          </Section>

          {/* ---- 5. Consentimento ---- */}
          <Section icon={Hand} title="5. Consentimento e coleta sob demanda">
            <p>
              Nenhum dado de sessão é coletado em segundo plano ou automaticamente. A coleta
              ocorre <strong className="text-foreground">somente após uma ação explícita</strong>
              {" "}sua: clicar no botão de conectar sessão dentro do popup da extensão. Enquanto
              você não acionar a captura, a extensão não lê nem envia seus cookies.
            </p>
          </Section>

          {/* ---- 6. Revogação ---- */}
          <Section icon={Power} title="6. Como revogar o acesso e desconectar">
            <p>Você pode encerrar o acesso a qualquer momento:</p>
            <ul className="space-y-2.5 pl-1">
              <Item>
                <strong className="text-foreground">Desconectar no painel:</strong> remova a
                credencial de sessão do Mercado Livre na área de credenciais do painel
                LucraLink. Isso apaga a sessão armazenada no Vault.
              </Item>
              <Item>
                <strong className="text-foreground">Encerrar a sessão no Mercado Livre:</strong>
                {" "}ao sair (logout) da sua conta do Mercado Livre, os cookies de sessão
                capturados deixam de ser válidos.
              </Item>
              <Item>
                <strong className="text-foreground">Remover a extensão:</strong> desinstale a
                extensão LucraLink Capture pelo gerenciador de extensões do Chrome para
                interromper qualquer coleta futura.
              </Item>
            </ul>
          </Section>

          {/* ---- 7. Contato ---- */}
          <Section icon={Mail} title="7. Contato">
            <p>
              Em caso de dúvidas sobre esta política ou sobre o tratamento dos seus dados,
              fale com a equipe do LucraLink pelo e-mail{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-primary underline-offset-4 hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>
        </article>
      </main>

      {/* ---- Footer ---- */}
      <footer className="border-t border-border/40 py-8 px-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <span className="font-bold tracking-tight text-sm text-muted-foreground">
            Lucra<span className="text-primary">Link</span>
          </span>
          <p>
            &copy; {new Date().getFullYear()} LucraLink. Todos os direitos reservados.
          </p>
          <Link to="/" className="hover:text-foreground transition-colors">
            Início
          </Link>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

function PolicyIntro(): JSX.Element {
  return (
    <>
      <header className="space-y-4">
        <span className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--gold)/0.3)] bg-[hsl(var(--gold)/0.08)] px-3 py-1 text-xs font-medium text-[hsl(var(--gold))]">
          <ShieldCheck size={13} />
          Privacidade e proteção de dados
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
          Política de Privacidade
        </h1>
        <p className="text-sm text-muted-foreground">
          Extensão <strong className="text-foreground">LucraLink Capture</strong> para
          navegador Google Chrome. Última atualização: {LAST_UPDATED}.
        </p>
      </header>

      <p className="text-base text-muted-foreground leading-relaxed">
        A extensão LucraLink Capture conecta a sua própria conta de afiliado do Mercado
        Livre à plataforma LucraLink, para que os seus links de afiliado sejam gerados
        automaticamente. Esta política descreve, de forma clara, quais dados a extensão
        coleta, para que servem, para onde vão, e como você pode revogar o acesso a
        qualquer momento.
      </p>
    </>
  );
}

interface SectionProps {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}

function Section({ icon: Icon, title, children }: SectionProps): JSX.Element {
  return (
    <section className="space-y-4">
      <h2 className="flex items-center gap-3 text-xl font-semibold tracking-tight">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Icon size={17} className="text-primary" />
        </span>
        {title}
      </h2>
      <div
        className={cn(
          "space-y-4 text-sm leading-relaxed text-muted-foreground",
          "pl-12",
        )}
      >
        {children}
      </div>
    </section>
  );
}

function Item({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <li className="relative pl-5">
      <span className="absolute left-0 top-2 h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))]" />
      {children}
    </li>
  );
}
